/**
 * Varint (LEB128) with zigzag — the decode half.
 *
 * Client code. The encoder lives in the data package, because it runs once at
 * build time; this runs in the browser on every load, so it is the half that
 * has to be fast and the half worth owning here.
 *
 * Each byte carries seven payload bits and one continuation flag, so a value
 * costs only as many bytes as its magnitude needs — 99.7% of daily close deltas
 * fit in one. Zigzag is applied before encoding because varint is unsigned and
 * a negative in two's complement has its high bits set; without it `-5` would
 * cost five bytes, and half of all deltas are negative.
 */

export const unzigzag = (v: number): number => (v >>> 1) ^ -(v & 1);

/**
 * Decode `count` values into a typed array.
 *
 * Sequential by necessity: a value's width is only known by walking the
 * continuation flags up to it, so there is no random access. That is fine for a
 * series read end to end, and it is why the caller passes a count rather than
 * the decoder discovering one.
 */
export const decodeVarints = (buf: Uint8Array, count: number): Int32Array => {
    const out = new Int32Array(count);
    let p = 0;
    for (let i = 0; i < count; i++) {
        let v = 0;
        let shift = 0;
        let byte: number;
        do {
            byte = buf[p++];
            v |= (byte & 127) << shift;
            shift += 7;
        } while (byte & 128);
        out[i] = unzigzag(v);
    }
    return out;
};

/** The blob rides inside a JS module rather than being fetched, so it arrives
    base64. Costs 33% before compression; brotli returns most of it. */
export const fromBase64 = (text: string): Uint8Array => {
    const binary = atob(text);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
};
